import { Play, Heart, Sparkles, Wind, Moon, Brain, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useBackdrop } from "../lib/backdrops";

const typeMeta = {
  visualization: { icon: Brain, tint: "from-royal to-turq", label: "Visualization" },
  affirmation: { icon: Sparkles, tint: "from-turq to-royal", label: "Affirmation" },
  meditation: { icon: Wind, tint: "from-royal/90 to-cyan-500", label: "Meditation" },
  frequency: { icon: Moon, tint: "from-navy to-royal", label: "Frequency" },
};

const fmt = (s) => {
  const m = Math.floor((s || 0) / 60);
  const sec = Math.round((s || 0) % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export const SessionCard = ({ item, hero = false }) => {
  const navigate = useNavigate();
  const meta = typeMeta[item.type] || typeMeta.visualization;
  const Icon = meta.icon;
  const backdrop = useBackdrop(item.content_id);

  if (item.pending) {
    return (
      <div data-testid={`session-card-${item.content_id}`} className={`w-full flex items-center gap-4 rounded-2xl bg-slate-50 border border-dashed border-slate-300 text-left ${hero ? "p-5" : "p-4"}`}>
        <div className="shrink-0 w-14 h-14 rounded-xl bg-slate-200 flex items-center justify-center text-slate-500"><Clock size={22} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-600">{item.release_at ? "Unlocks on your next training day" : "Being finalized by Coach"}</div>
          <div className="font-serif font-bold text-navy truncate">{item.title}</div>
          <div className="text-xs text-slate-500 mt-0.5" data-testid={`release-note-${item.content_id}`}>
            {item.release_at ? `Available ${new Date(item.release_at).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}` : "Available within 24 hours or less"}
          </div>
        </div>
      </div>
    );
  }

  if (hero) {
    const isViz = item.type === "visualization";
    return (
      <button data-testid={`session-card-${item.content_id}`}
        onClick={() => navigate(`/player/${item.content_id}`)}
        className="relative w-full text-left rounded-3xl overflow-hidden bg-navy text-white p-6 min-h-[196px] shadow-xl shadow-navy/20 active:scale-[0.99] transition-transform">
        {isViz ? (
          <>
            <img src={backdrop.file} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: backdrop.pos }} />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/75 to-navy/25" />
          </>
        ) : (
          <>
            <div className={`absolute inset-0 bg-gradient-to-br ${meta.tint} opacity-90`} />
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          </>
        )}
        <div className="relative z-10 flex flex-col justify-end min-h-[148px]">
          <div className="flex items-center gap-2 text-white/80 text-xs font-semibold uppercase tracking-widest">
            <Icon size={15} /> Your MindMoves — Today
          </div>
          <h3 className="font-serif text-2xl font-bold mt-3 leading-tight">{item.title}</h3>
          <p className="text-white/80 text-sm mt-1">{item.category} · {fmt(item.duration)}</p>
          <div className="mt-5 inline-flex items-center gap-2 bg-white text-navy font-bold px-5 py-2.5 rounded-full text-sm">
            <Play size={16} fill="currentColor" /> Begin session
          </div>
        </div>
      </button>
    );
  }

  return (
    <button data-testid={`session-card-${item.content_id}`}
      onClick={() => navigate(`/player/${item.content_id}`)}
      className="w-full text-left flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-3.5 hover:border-royal/50 hover:shadow-md transition-all active:scale-[0.99]">
      <div className={`shrink-0 w-14 h-14 rounded-xl overflow-hidden relative bg-gradient-to-br ${meta.tint} flex items-center justify-center text-white`}>
        {item.type === "visualization" && <img src={backdrop.file} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />}
        <Icon size={22} className="relative drop-shadow" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-bold uppercase tracking-wider text-royal">{meta.label}</div>
        <div className="font-bold text-navy truncate">{item.title}</div>
        <div className="text-xs text-slate-500">{item.category} · {fmt(item.duration)}</div>
      </div>
      {item.hearted && <Heart size={18} className="text-rose-500 fill-rose-500 shrink-0" />}
      <div className="shrink-0 w-9 h-9 rounded-full bg-royal/10 flex items-center justify-center">
        <Play size={16} className="text-royal" fill="currentColor" />
      </div>
    </button>
  );
};

export default SessionCard;
